import {
  PropositionType,
  type ChatMessage,
  type CreateChatMessage,
} from "@cryptors/api-client";
import { ArrowBack, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { IResourceComponentsProps } from "@refinedev/core";
import { Create, Edit, SaveButtonProps, useAutocomplete } from "@refinedev/mui";
import { FC, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  useGetChatMessage,
  usePostChatMessage,
  usePatchChatMessage,
} from "@/api/chat-message.api";
import UploadButton from "@/components/forms/UploadButton";
import { getTranslation } from "@/utils/translation";
import { upload } from "@/utils/upload";

import {
  ChatAnswerDetailsFormValue,
  ChatMessageFormValues,
  ChatPropositionFormValue,
  ChatQuestionFormValue,
} from "./chat-message-form.types";
import ChatMessageChildrenSelector from "./ChatMessageChildrenSelector";
import QuestionReward from "./QuestionReward";

interface ICreateEdit {
  type: "create" | "edit";
  isLoading: boolean;
  saveButtonProps: SaveButtonProps;
  children: React.ReactNode;
}

const CreateEdit: FC<ICreateEdit> = ({ type, children, ...rest }) => {
  const { cryptorId } = useParams();
  const navigate = useNavigate();
  if (type === "create") {
    return <Create {...rest}>{children}</Create>;
  } else if (type === "edit") {
    return (
      <Edit
        {...rest}
        goBack={
          <IconButton
            onClick={() => navigate(`/cryptors/${cryptorId}/chat-message`)}
          >
            <ArrowBack />
          </IconButton>
        }
      >
        {children}
      </Edit>
    );
  }
  return null;
};

interface IChatMessageCreateEdit extends IResourceComponentsProps {
  type: "create" | "edit";
}

export const ChatMessageCreateEdit: FC<IChatMessageCreateEdit> = ({ type }) => {
  const { cryptorId, id } = useParams();
  const navigate = useNavigate();
  const [questionQuizSelected, setQuestionQuizSelected] =
    useState<"question">();

  const { data, isFetched } = useGetChatMessage(
    Number(cryptorId),
    id as string,
  );
  const { mutateAsync: postChatMessage } = usePostChatMessage(
    Number(cryptorId),
  );
  const { mutateAsync: patchChatMessage } = usePatchChatMessage(
    Number(cryptorId),
    id as string,
  );

  const {
    setValue,
    watch,
    getValues,
    handleSubmit,
    register,
    setError,
    control,
    reset,
    formState: { isLoading: formLoading, isDirty, isValid, errors },
  } = useForm<ChatMessageFormValues>({
    defaultValues: {
      isEntryPoint: false,
      shouldTriggerQuiz: false,
      image: { url: null },
      audio: { url: null },
      video: { url: null },
      question: { type: PropositionType.CHOICE, propositions: [] },
    },
  }); // TODO: setup zod validation

  const {
    append: questionPropositionsAppend,
    remove: questionPropositionsRemove,
  } = useFieldArray({
    control,
    name: "question.propositions",
    rules: {
      validate: (value) => {
        if (questionQuizSelected !== "question") {
          return true;
        }
        if (getValues("question")) {
          return value?.length > 0;
        }
      },
    },
  });

  useEffect(() => {
    if (!data) return;
    reset({
      isEntryPoint: data.isEntryPoint,
      shouldTriggerQuiz: data.shouldTriggerQuiz,
      text: getTranslation(data?.textTranslation)?.value,
      video: { url: data.video?.url ?? null },
      image: { url: data.image?.url ?? null },
      audio: { url: data.audio?.url ?? null },
      chatMessageChildrenId: data?.chatMessageChildrenId,
      question: {
        type: data.question?.type ?? PropositionType.CHOICE,
        goodAnswerDetails: {
          description: getTranslation(
            data?.question?.goodAnswerDetails?.descriptionTranslation,
          )?.value,
        },
        badAnswerDetails: {
          description: getTranslation(
            data?.question?.badAnswerDetails?.descriptionTranslation,
          )?.value,
        },
        propositions:
          data.question?.propositions.map((proposition) => ({
            text: getTranslation(proposition.textTranslation)?.value,
            isCorrect: proposition.isCorrect,
            chatMessageId: proposition.chatMessageId,
          })) ?? [],
      },
    });
    setQuestionQuizSelected(
      (data?.question?.propositions?.length || 0) > 0 ? "question" : undefined,
    );
  }, [data, isFetched, reset]);

  useEffect(() => {
    if (questionQuizSelected === "question") {
      setValue(
        "question.text",
        getTranslation(data?.question?.textTranslation)?.value,
      );
      data?.question?.propositions.forEach((proposition, pIndex) => {
        setValue(
          `question.propositions.${pIndex}.text`,
          getTranslation(proposition.textTranslation)?.value,
        );
      });
    }
  }, [data, questionQuizSelected, setValue]);

  useEffect(() => {
    setQuestionQuizSelected(data?.question ? "question" : undefined);
  }, [data?.question, formLoading]);

  const { autocompleteProps: chatMessageAutocompleteProps } =
    useAutocomplete<ChatMessage>({
      resource: "chat-message",
      filters: cryptorId
        ? [{ field: "cryptorId", operator: "eq", value: Number(cryptorId) }]
        : undefined,
      defaultValue: data?.chatMessageChildrenId || undefined,
      pagination: { pageSize: 1000, mode: "server" },
    });

  const onFinishHandler = async (data: ChatMessageFormValues) => {
    if (questionQuizSelected !== "question") data.question = undefined;

    const answerDetail = (
      detail: ChatAnswerDetailsFormValue | null | undefined,
    ) => {
      if (detail?.description) {
        return {
          id: detail.id,
          descriptionTranslation: { value: detail.description },
        };
      }
      return null;
    };

    const questionBase = (question: ChatQuestionFormValue) => ({
      id: question.id || undefined,
      textTranslation: { value: question.text },
      type: question.type,
      reward: question.reward,
      goodAnswerDetails: answerDetail(question.goodAnswerDetails),
      badAnswerDetails: answerDetail(question.badAnswerDetails),
    });

    const propositionBase = (proposition: ChatPropositionFormValue) => {
      if (proposition.text) {
        return {
          id: proposition.id,
          isCorrect: proposition.isCorrect,
          textTranslation: { value: proposition.text },
        };
      }
      return null;
    };

    const body: CreateChatMessage & { quiz?: unknown } = {
      isEntryPoint: data.isEntryPoint,
      shouldTriggerQuiz: data.shouldTriggerQuiz,
      textTranslation: { value: data.text },
      image: data?.image?.url ? { url: data.image.url } : null,
      video: data?.video?.url ? { url: data.video.url } : null,
      audio: data?.audio?.url ? { url: data.audio.url } : null,
      question: data.question
        ? {
            ...questionBase(data.question),
            propositions: data.question.propositions.flatMap((proposition) => {
              const base = propositionBase(proposition);
              return base
                ? [
                    {
                      ...base,
                      chatMessageId: proposition.chatMessageId || null,
                    },
                  ]
                : [];
            }),
          }
        : undefined,
      quiz: data.quiz
        ? {
            id: data.quiz.id ? data.quiz.id : undefined,
            reward: data.quiz.reward,
            timerQuestion: data.quiz.timerQuestion,
            chatMessageChildren80PlusId:
              data.quiz.chatMessageChildren80PlusId || null,
            chatMessageChildren50PlusId:
              data.quiz.chatMessageChildren50PlusId || null,
            chatMessageChildren50LessId:
              data.quiz.chatMessageChildren50LessId || null,
            questions: data.quiz.questions.map((question) => ({
              ...questionBase(question),
              propositions: question.propositions.map(propositionBase),
            })),
          }
        : undefined,
      chatMessageChildrenId: data.chatMessageChildrenId || undefined,
    };

    if (type === "create") {
      postChatMessage(body, {
        onSuccess: (response) => {
          navigate(`/cryptors/${cryptorId}/chat-message/edit/${response.id}`);
        },
      });
    } else if (type === "edit") {
      patchChatMessage(body);
    }
  };

  return (
    <CreateEdit
      type={type}
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Controller
          control={control}
          name="isEntryPoint"
          defaultValue={false}
          render={({ field }) => {
            return (
              <FormControlLabel
                checked={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                control={<Checkbox />}
                label="Est le point d'entrée"
              />
            );
          }}
        />
        <Controller
          control={control}
          name="shouldTriggerQuiz"
          defaultValue={false}
          render={({ field }) => {
            return (
              <FormControlLabel
                checked={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                control={<Checkbox />}
                label="Déclenchement du quiz"
              />
            );
          }}
        />

        <TextField
          {...register("text")}
          defaultValue={null}
          error={!!errors.text}
          helperText={errors.text?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Texte"
        />
        <TextField
          {...register("video.url", {
            validate: (value) => {
              if (!value) return true;
              try {
                const url = new URL(value);
                if (
                  url.host !== "www.youtube.com" ||
                  !url.searchParams.get("v")
                ) {
                  return false;
                }
                return true;
              } catch {
                return false;
              }
            },
          })}
          defaultValue={null}
          error={!!errors.video?.url}
          helperText={errors.video?.url?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Video URL Youtube"
        />
        <Controller
          control={control}
          name="image.url"
          defaultValue={null}
          render={({ field, fieldState }) => (
            <UploadButton
              id="image"
              inputSrc={field.value}
              label="Image"
              labelWidth={50}
              onError={() =>
                setError("image.url", {
                  message: "L'upload a échoué. Veuillez réessayer",
                })
              }
              onChange={async (event) => {
                const url = await upload(event, "chatAssets/image");
                field.onChange(url);
              }}
              onDelete={() => field.onChange(null)}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="audio.url"
          defaultValue={null}
          render={({ field, fieldState }) => (
            <UploadButton
              id="audio"
              inputSrc={field.value}
              label="Son"
              labelWidth={50}
              type="audio"
              onError={() =>
                setError("audio.url", {
                  message: "L'upload a échoué. Veuillez réessayer",
                })
              }
              onChange={async (event) => {
                const url = await upload(event, "chatAssets/audio");
                field.onChange(url);
              }}
              onDelete={() => field.onChange(null)}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
        <ChatMessageChildrenSelector
          control={control}
          name="chatMessageChildrenId"
          autocompleteProps={chatMessageAutocompleteProps}
        />
        <Divider sx={{ margin: 4 }}>
          <Stack
            direction="row"
            justifyContent="space-evenly"
            width={500}
            gap={2}
          >
            <Button
              fullWidth
              variant={
                questionQuizSelected === "question" ? "contained" : "outlined"
              }
              color="primary"
              onClick={() => {
                if (questionQuizSelected === "question") {
                  setQuestionQuizSelected(undefined);
                } else {
                  setQuestionQuizSelected("question");
                }
              }}
            >
              Question
            </Button>
          </Stack>
        </Divider>
        {questionQuizSelected === "question" && (
          <>
            <Controller
              control={control}
              name="question.type"
              render={({ field }) => (
                <FormControl margin={"normal"}>
                  <InputLabel htmlFor="outlined-adornment">Type</InputLabel>
                  <Select {...field} label="Ville">
                    {[
                      { label: "Choix", value: PropositionType.CHOICE },
                      { label: "Question", value: PropositionType.QUESTION },
                    ].map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <QuestionReward
              control={control}
              textName="question.text"
              rewardName="question.reward"
            />
            {watch("question.propositions")?.map((proposition, index) => {
              return (
                <Stack
                  key={proposition.id ?? `new-proposition-${index}`}
                  direction="row"
                  alignItems="center"
                  gap={3}
                >
                  <TextField
                    {...register(`question.propositions.${index}.text`)}
                    defaultValue={null}
                    error={!!errors.question?.propositions?.[index]?.text}
                    helperText={
                      errors.question?.propositions?.[index]?.text?.message
                    }
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label={`Réponse ${index + 1}`}
                  />
                  <Controller
                    control={control}
                    name={`question.propositions.${index}.isCorrect`}
                    defaultValue={false}
                    render={({ field }) => {
                      return (
                        <FormControlLabel
                          checked={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          control={<Checkbox />}
                          label="Est correct"
                          sx={{ minWidth: 125 }}
                        />
                      );
                    }}
                  />
                  <ChatMessageChildrenSelector
                    control={control}
                    name={`question.propositions.${index}.chatMessageId`}
                    autocompleteProps={chatMessageAutocompleteProps}
                  />
                  <IconButton onClick={() => questionPropositionsRemove(index)}>
                    <Close />
                  </IconButton>
                </Stack>
              );
            })}
            <Button
              onClick={() =>
                questionPropositionsAppend({ text: "", isCorrect: false })
              }
            >
              Ajouter une proposition
            </Button>
            <TextField
              {...register(`question.goodAnswerDetails.description`)}
              defaultValue={null}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Détails de la bonne réponse"
            />
            <TextField
              {...register(`question.badAnswerDetails.description`)}
              defaultValue={null}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              type="text"
              label="Détails de la mauvaise réponse"
            />
          </>
        )}
      </Box>
    </CreateEdit>
  );
};
